import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Lock, LockOpen, Trash2 } from 'lucide-react';

interface SortableColorSwatchProps {
  id: string;
  color: string;
  index: number;
  onRemove: (index: number) => void;
  onKeyboardMove: (index: number, direction: 'left' | 'right') => void;
  onToggleLock?: (index: number) => void;
  isLocked?: boolean;
  isReadOnly?: boolean;
}

export function SortableColorSwatch({
  id,
  color,
  index,
  onRemove,
  onKeyboardMove,
  onToggleLock,
  isLocked = false,
  isReadOnly = false,
}: SortableColorSwatchProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isReadOnly || isLocked });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Alt+Arrow keys for keyboard reordering
    if (e.altKey) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onKeyboardMove(index, 'left');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onKeyboardMove(index, 'right');
      }
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative"
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Color swatch ${color}. Press Alt+Left or Alt+Right to reorder.`}
    >
      <div
        className={`w-full aspect-square rounded-lg shadow-md border-2 transition-all ${
          isDragging
            ? 'border-blue-500 ring-2 ring-blue-300 dark:ring-blue-700'
            : isLocked
            ? 'border-yellow-500 dark:border-yellow-400'
            : 'border-gray-200 dark:border-gray-600'
        } ${!isReadOnly && !isLocked ? 'cursor-grab active:cursor-grabbing hover:scale-105' : ''}`}
        style={{ backgroundColor: color }}
      >
        {!isReadOnly && !isLocked && (
          <div
            {...attributes}
            {...listeners}
            className="absolute top-1 left-1 p-1 bg-white/80 dark:bg-gray-800/80 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
            aria-label="Drag to reorder"
          >
            <GripVertical className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          </div>
        )}
        {isLocked && (
          <div className="absolute top-1 left-1 p-1 bg-yellow-500/90 dark:bg-yellow-600/90 rounded">
            <Lock className="w-4 h-4 text-white" />
          </div>
        )}
      </div>
      {!isReadOnly && onToggleLock && (
        <button
          onClick={() => onToggleLock(index)}
          className={`absolute bottom-1 left-1 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
            isLocked
              ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
              : 'bg-gray-500 hover:bg-gray-600 text-white'
          }`}
          aria-label={isLocked ? `Unlock color ${color}` : `Lock color ${color}`}
          title={isLocked ? 'Unlock (allow shuffling)' : 'Lock (prevent shuffling)'}
        >
          {isLocked ? <Lock className="w-3 h-3" /> : <LockOpen className="w-3 h-3" />}
        </button>
      )}
      {!isReadOnly && (
        <button
          onClick={() => onRemove(index)}
          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
          aria-label={`Remove color ${color}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
      <p className="text-xs text-center mt-1 font-mono text-gray-700 dark:text-gray-300">
        {color}
      </p>
    </div>
  );
}
